import memoize from 'memoize-one';

import {ResourceMap, ResourceTable} from '../../src/store/appTypes';

export function createObjectPropertyUpdater<T extends object, K extends keyof T>(
  propertyName: K,
  updatePropertyValue: (value: T[K]) => T[K],
): (object: T) => T {
  return createUpdater((object) => {
    const value = object[propertyName];
    const newValue = updatePropertyValue(value);
    if (value === newValue) {
      return object;
    }

    return {
      ...object,
      [propertyName]: newValue,
    };
  });
}

export function createResourceMapValueUpdater<T>(
  resourceId: number | string,
  updateResource: (resource: T) => T,
): (resources: ResourceMap<T>) => ResourceMap<T> {
  return createUpdater((resources) => {
    const resource = resources[resourceId];
    if (!resource) {
      return resources;
    }

    const newResource = updateResource(resource);
    if (resource === newResource) {
      return resources;
    }

    return {
      ...resources,
      [resourceId]: newResource,
    };
  });
}

export function createResourceTableValueUpdater<TId extends string | number, TResource>(
  resourceId: TId,
  updateResource: (resource: TResource) => TResource,
): (resources: ResourceTable<TId, TResource>) => ResourceTable<TId, TResource> {
  return createObjectPropertyUpdater('byId', createResourceMapValueUpdater(resourceId, updateResource));
}

export function createUpdater<T>(update: (value: T) => T): (value: T) => T {
  return memoize(update);
}
